// BeginCheckout.jsx
import React, { useEffect, useState, useRef } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { Elements, useStripe, useElements } from "@stripe/react-stripe-js"
import AldooStoreAPI from "aldoo-ra/Store/api"
import { api } from "aldoo-ra/API"
import Value from "aldoo-ra/Value"
import { WalletIcon } from "@heroicons/react/24/outline"
import Icon from "aldoo-ra/Icon"
import ArrowMenuMobile from "aldoo-ra/ArrowMenuMobile"
import PurchaseFlowController from "aldoo-ra/Store/PurchaseFlowController"
import { getStripePromise } from "aldoo-ra/Store/stripe-client"

// Publishable key comes from the server (ParamIO) via store_config —
// Stripe.js still downloads lazily on first render of this element
const stripePromise = () => getStripePromise()
let paymentLoading = false

function CheckoutContent({ store, controller }) {
  const texts = TextBundle({ id: "aldoo-store" })
  const [paymentInfo] = Value("aldoo-payment-info")
  const [preloadedInvoiceData, setPreloadedInvoiceData] = Value("aldoo-preloaded-invoice-data")
  const [showingNewPayment, setShowingNewPayment] = useState(false)
  const [showingInvoice, setShowingInvoice] = useState(false)
  // Initialize with preloaded data if available
  const [invoiceData, setInvoiceData] = useState(() => preloadedInvoiceData || null)
  const [invoiceStepWasShown, setInvoiceStepWasShown] = useState(false)

  // Use controller state instead of local state
  const currentStep = controller.currentStep
  const { canContinue, loading, continueText, hideButtons, handleContinue, handleBack, getFlowSteps, isStepActive } = controller

  const {
    cart,
    setPaymentInfo,
    checkoutVisible,
    emailDialogVisible,
    hideCheckout,
    getElement,
    getFragment,
    cartChanged,
    purchaseFlow,
  } = store

  // Get components
  const CheckoutForm = getElement("checkout-form")
  const ReusePaymentDialog = getElement("reuse-payment-dialog")
  const InvoiceStep = getElement("invoice-step")
  const EmailInputDialog = getElement("email-input-dialog")
  const CheckoutWithInvoice = getElement("checkout-with-invoice")
  const CheckoutPreparingPayment = getFragment("checkout-preparing-payment")
  const CheckoutContainer = getFragment("checkout-container")

  // Initialize invoice data from preloaded data before flow starts
  useEffect(() => {
    if (checkoutVisible && preloadedInvoiceData && !invoiceData) {
      setInvoiceData({
        ...preloadedInvoiceData,
        useSavedBillingDetails: true
      })
      setPreloadedInvoiceData(null)
    }
  }, [checkoutVisible, preloadedInvoiceData])

  // Flow initialization is now handled by PurchaseFlowController

  // Reset showingNewPayment when returning to payment-reuse step
  useEffect(() => {
    if (currentStep === 'payment-reuse' && showingNewPayment) {
      setShowingNewPayment(false)
    }
  }, [currentStep])

  // Remove focus from any focused button when step changes
  useEffect(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
  }, [currentStep])

  useEffect(() => {
    const validateAndRefreshPaymentInfo = async () => {
      if (!cart || cart.length === 0) return
      if (paymentLoading) return

      // Check if we have cached payment info but need to validate the saved payment method
      if (paymentInfo && !cartChanged && paymentInfo.savedPaymentMethod) {
        // Check if the saved payment method is expired or stale
        const savedMethod = paymentInfo.savedPaymentMethod
        const currentYear = new Date().getFullYear()
        const currentMonth = new Date().getMonth() + 1

        // If card is expired or close to expiry, force refresh
        const isExpiredOrExpiring = savedMethod.type === 'card' &&
          (savedMethod.expiryYear < currentYear ||
           (savedMethod.expiryYear === currentYear && savedMethod.expiryMonth <= currentMonth))

        if (isExpiredOrExpiring) {
          console.log("BeginCheckout - Saved payment method is expired/expiring, forcing refresh")
          // Force refresh by not returning early
        } else {
          // Validate with server to ensure we have the latest payment method
          try {
            const currentMethods = await AldooStoreAPI.getPaymentMethods()
            const currentDefaultMethod = currentMethods.find(method => method.isDefault)

            if (!currentDefaultMethod || currentDefaultMethod.id !== savedMethod.id ||
                currentDefaultMethod.expiryYear !== savedMethod.expiryYear ||
                currentDefaultMethod.expiryMonth !== savedMethod.expiryMonth) {
              console.log("BeginCheckout - Saved payment method differs from server, forcing refresh")
              console.log("Cached method:", savedMethod)
              console.log("Server method:", currentDefaultMethod)
            } else {
              setPaymentInfo({ ...paymentInfo })
              return
            }
          } catch (error) {
            console.error("Failed to validate payment methods:", error)
            // Continue with refresh on error
          }
        }
      }

      paymentLoading = true
      const cartItem = cart[0]

      try {
        let response

        // Use custom auth if available (for existing users not logged in)
        if (store.customAuth) {
          const storeAPIWithAuth = api("store", store.customAuth)
          response = await storeAPIWithAuth({
            initiate_payment: {
              platform: "Web",
              pid: cartItem.pid,
              promo_code: store.promoCode,
            }
          })
        } else {
          response = await AldooStoreAPI.initializePayment({
            pid: cartItem.pid,
            promo_code: store.promoCode,
          })
        }

        paymentLoading = false

        if (response.error) {
          console.error("Failed to initialize payment:", response.error)
          return
        }

        response.pid = cartItem.pid
        response.promoCode = store.promoCode

        setPaymentInfo(response)
      } catch (error) {
        console.error("Failed to initialize payment:", error)
        paymentLoading = false
      }
    }

    if (checkoutVisible) {
      // Invoice data loading is now handled in a separate useEffect above

      // Initialize payment info when checkout opens IF user is authenticated
      // This ensures billing details are available for flow initialization
      // For non-authenticated users, payment info will load after email step
      const hasAuth = store.auth || store.tempAuthForPurchase
      if (!paymentInfo && hasAuth) {
        validateAndRefreshPaymentInfo()
      }

      // Reset invoice step tracking when opening a new checkout session
      setInvoiceStepWasShown(false)
    } else {
      // Reset invoice data when checkout is closed
      setInvoiceData(null)
      setShowingInvoice(false)
      setShowingNewPayment(false)
    }
  }, [checkoutVisible, invoiceData, preloadedInvoiceData, store.auth, store.tempAuthForPurchase])

  // Listen for payment methods updates and refresh payment info
  useEffect(() => {
    const handlePaymentMethodsUpdated = () => {
      console.log("BeginCheckout - payment-methods-updated event received, refreshing payment info")
      // Force re-initialization of payment info to get latest saved payment method
      if (checkoutVisible && cart && cart.length > 0) {
        const cartItem = cart[0]

        // Use custom auth if available (for existing users not logged in)
        const initPayment = store.customAuth
          ? api("store", store.customAuth)({
              initiate_payment: {
                platform: "Web",
                pid: cartItem.pid,
                promo_code: store.promoCode,
              }
            })
          : AldooStoreAPI.initializePayment({
              pid: cartItem.pid,
              promo_code: store.promoCode,
            })

        initPayment.then(response => {
          if (!response.error) {
            response.pid = cartItem.pid
            response.promoCode = store.promoCode
            console.log("BeginCheckout - refreshed payment info after payment method update:", response)
            setPaymentInfo(response)
          }
        }).catch(error => {
          console.error("Failed to refresh payment info:", error)
        })
      }
    }

    window.addEventListener("payment-methods-updated", handlePaymentMethodsUpdated)
    return () => {
      window.removeEventListener("payment-methods-updated", handlePaymentMethodsUpdated)
    }
  }, [checkoutVisible, cart, store.promoCode])

  if (paymentInfo && !paymentInfo.requiresPayment) return null
  if (!checkoutVisible) return null

  const shouldShowReuseDialog =
    currentStep === 'payment-reuse' &&
    paymentInfo?.savedPaymentMethod &&
    !showingNewPayment &&
    !showingInvoice

  // Show invoice step if:
  // - We don't have invoiceData yet (user hasn't filled it)
  // - Always show first to give user option to request invoice
  const shouldShowInvoiceFirst = !showingInvoice && !invoiceData

  // All step navigation is now handled by PurchaseFlowController via PubSub

  const handleShowInvoice = () => {
    setShowingInvoice(true)
    // Mark that invoice step was shown since user manually opened it
    setInvoiceStepWasShown(true)
  }

  const handlePaymentComplete = async () => {
    // Complete payment with stored invoice data
    if (store.completePaymentWithInvoice) {
      await store.completePaymentWithInvoice(invoiceData)
    } else {
      store.onPaymentSuccess()
    }
  }

  // Store invoice data in store for access by payment submission
  if (invoiceData && !store.invoiceData) {
    store.invoiceData = invoiceData
  }

  // Get all flow steps and current index
  // Use getFlowSteps from controller
  const flowSteps = getFlowSteps()

  const getCurrentStepIndex = () => {
    if (!purchaseFlow) return -1
    // If current step is payment-reuse, treat it as payment for display purposes
    const displayStep = currentStep === 'payment-reuse' ? 'payment' : currentStep
    return flowSteps.findIndex(s => s.id === displayStep)
  }

  // Render flow progress header
  const renderFlowProgress = (interactive = false) => {
    const steps = flowSteps
    const currentIndex = getCurrentStepIndex()

    if (steps.length === 0) return null

    return (
      <>
        {/* Mobile version - using ArrowMenuMobile component */}
        <div className="block md:hidden -mx-6 -mt-6 mb-4">
          <ArrowMenuMobile
            menuData={steps}
            selectedIndex={currentIndex}
            interactive={interactive}
            enableScrollNavigation={false}
            fixedPosition={false}
            itemRender={(item, index, isSelected) => {
                const isPast = index < currentIndex
                return (
                  <div
                    className={`w-[150px] h-[40px] flex items-center justify-center transition-all duration-300 relative origin-center
                      ${isSelected ? 'text-primary font-bold scale-105' : ''}
                      ${isPast ? 'text-primary scale-100' : ''}
                      ${!isSelected && !isPast ? 'text-text_secondary scale-100' : ''}
                    `}
                  >
                    {isPast && (
                      <svg className="w-4 h-4 text-primary flex-shrink-0 absolute left-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    <span className="text-sm text-center whitespace-nowrap">
                      {item.name}
                    </span>
                  </div>
                )
              }}
            />
        </div>

        {/* Desktop version - original horizontal menu */}
        <div className="hidden md:flex items-center mb-4 py-4">
          {steps.map((step, index) => {
            const isActive = index === currentIndex
            const isPast = index < currentIndex
            const isLast = index === steps.length - 1

            return (
              <React.Fragment key={step.id}>
                <div className="flex-1 flex items-center justify-center gap-2">
                  {isPast && (
                    <svg className="w-4 h-4 text-primary flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  <div
                    className={`
                      text-sm md:text-base font-medium text-center whitespace-nowrap transition-all pb-1
                      ${isActive ? 'text-text border-b-4 border-primary font-semibold' : ''}
                      ${isPast ? 'text-primary' : ''}
                      ${!isActive && !isPast ? 'text-text' : ''}
                    `}
                  >
                    {step.name}
                  </div>
                </div>
                {!isLast && (
                  <div className="w-px h-6 bg-bordered flex-shrink-0"></div>
                )}
              </React.Fragment>
            )
          })}
        </div>
      </>
    )
  }

  // Determine modal title based on current step
  const getModalTitle = () => {
    if (currentStep === 'email') return texts?.enterYourEmail || 'Enter Your Email'
    if (currentStep === 'invoice') return texts?.invoiceDetails || 'Invoice details'
    return texts?.payment || 'Payment'
  }

  // Render unified footer with Back/Continue buttons
  const renderFooter = () => {
    // Determine if we're on a payment step
    const isPaymentStep = currentStep === 'payment' || currentStep === 'payment-reuse'
    const buttonText = continueText

    // Show wallet icon only when button text indicates a payment action (not "Continue")
    // This ensures icon shows for "Pay Now" but not when selecting "add new payment method"
    const isPaymentAction = buttonText && (
      buttonText.toLowerCase().includes('pay') ||
      buttonText.toLowerCase().includes('submit')
    )
    const showWalletIcon = isPaymentStep && canContinue && !loading && isPaymentAction

    // Hide buttons when magic link verification is showing
    if (hideButtons) {
      return null
    }

    return (
      <div className="flex gap-3 mt-4">
        <Button
          variant="secondary"
          onClick={(e) => {
            e.currentTarget.blur() // Remove focus after click
            handleBack()
          }}
          disabled={loading}
          className="flex-1 !h-auto p-4"
        >
          {texts?.back || "Back"}
        </Button>

        <Button
          variant="primary"
          onClick={(e) => {
            e.currentTarget.blur() // Remove focus after click
            handleContinue()
          }}
          disabled={!canContinue || loading}
          className={`flex-1 !h-auto p-4 gap-2 relative ${!canContinue || loading ? 'opacity-50' : ''}`}
        >
          {loading && (
            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          {showWalletIcon ? (
            <WalletIcon
              className="h-5 w-5 text-white animate-slideInFromLeft"
              key="wallet-in"
            />
          ) : (
            isPaymentStep && !canContinue && (
              <WalletIcon
                className="h-5 w-5 text-white animate-slideOutToRight"
                key="wallet-out"
              />
            )
          )}
          {loading ? (currentStep === 'email' ? (texts?.verifying || "Verifying...") : (texts?.loading || "Loading...")) : buttonText}
        </Button>
      </div>
    )
  }

  // Render all steps but show/hide based on currentStep to preserve Stripe form state
  const renderAllSteps = () => {
    return (
      <>
        {/* Email step - doesn't need payment info */}
        <div className={currentStep === 'email' ? '' : 'hidden'}>
          <EmailInputDialog
            store={store}
            initialEmail={store.auth?.email || store.tempAuthForPurchase?.email || store.purchaseUserEmail || ""}
            isActive={isStepActive('email')}
          />
        </div>

        {/* Invoice step - wrapped in Elements for Stripe AddressElement */}
        <div className={currentStep === 'invoice' ? 'h-full' : 'hidden'}>
          {paymentInfo && paymentInfo.clientSecret ? (
            <Elements
              stripe={stripePromise()}
              options={{
                clientSecret: paymentInfo.clientSecret,
              loader: "always",
              appearance: {
                variables: {
                  colorDanger: "#df1b41",
                  colorBackground: "#f9f9f9",
                  colorText: "#090909",
                },
              },
            }}
          >
            <CheckoutWithInvoice store={store} invoiceData={invoiceData}>
              <InvoiceStep
                store={store}
                isOpen={currentStep === 'invoice'}
              />
            </CheckoutWithInvoice>
          </Elements>
          ) : (
            <CheckoutPreparingPayment />
          )}
        </div>

        {/* Payment step - wrapped in Elements to preserve PaymentElement state */}
        <div className={(currentStep === 'payment' || currentStep === 'payment-reuse') ? 'h-full' : 'hidden'}>
          {paymentInfo && paymentInfo.clientSecret ? (
            <Elements
              stripe={stripePromise()}
              options={{
                clientSecret: paymentInfo.clientSecret,
              loader: "always",
              appearance: {
                variables: {
                  colorDanger: "#df1b41",
                  colorBackground: "#f9f9f9",
                  colorText: "#090909",
                },
              },
            }}
          >
            <CheckoutWithInvoice store={store} invoiceData={invoiceData}>
              {shouldShowReuseDialog ? (
                <div className="h-full flex flex-col justify-between">
                  <ReusePaymentDialog
                    store={store}
                    onSelectNewPayment={() => setShowingNewPayment(true)}
                    isActive={isStepActive('payment-reuse')}
                  />
                </div>
              ) : (
                <CheckoutForm
                  store={store}
                  isActive={isStepActive('payment')}
                />
              )}
            </CheckoutWithInvoice>
          </Elements>
          ) : (
            <CheckoutPreparingPayment />
          )}
        </div>
      </>
    )
  }

  // Check if we're still loading initial data
  const isLoadingInitialData = checkoutVisible && currentStep !== 'email' && !paymentInfo

  // Single unified modal that shows different steps
  return (
    <Modal
      isOpen={checkoutVisible || emailDialogVisible}
      variant="store_checkout"
      className="md:w-[750px] md:!max-h-[85vh]"
      onClose={hideCheckout}
      allowCloseBtn={false}
    >
      <div className="flex flex-col h-full md:h-auto md:max-h-[calc(85vh-2rem)]">
        <div className="flex-shrink-0">
          {renderFlowProgress(false)}
        </div>
        <div className="flex-1 bg-paper rounded-2xl border border-gray-100 min-h-0 relative overflow-y-auto scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-paper">
          {isLoadingInitialData ? (
            <CheckoutPreparingPayment />
          ) : (currentStep === 'email' || currentStep === 'invoice') ? (
            <div className="min-h-full flex items-center justify-center py-4">
              <CheckoutContainer store={store}>
                {renderAllSteps()}
              </CheckoutContainer>
            </div>
          ) : (
            <CheckoutContainer store={store}>
              {renderAllSteps()}
            </CheckoutContainer>
          )}
        </div>
        <div className="flex-shrink-0 pb-safe">
          {renderFooter()}
        </div>
      </div>
    </Modal>
  )
}

// Main component that wraps CheckoutContent with PurchaseFlowController
export default function BeginCheckout({ store }) {
  return (
    <PurchaseFlowController store={store}>
      {(controller) => <CheckoutContent store={store} controller={controller} />}
    </PurchaseFlowController>
  )
}
